/**
 * The roles the backend assigns. The strings are the wire values returned by
 * `GET /api/auth/is-authenticated` (`role`) and `GET /api/user/profile`
 * (`roles[0]`). `Dev` is not a role here: the developer portal is a separate
 * JWT session tracked by `AuthService.isDev`.
 */
export enum UserRole {
  User = 'User',
  Admin = 'Admin',
  SuperAdmin = 'SuperAdmin',
}

/**
 * Parses a wire value. Anything unrecognised is `null`, deliberately NOT
 * `UserRole.User`: defaulting an unknown role to the lowest one would show an
 * unexpected account a User's screens, and a future role would be mislabelled
 * rather than visibly unhandled.
 */
export function parseUserRole(raw: unknown): UserRole | null {
  switch (raw) {
    case UserRole.User:
    case UserRole.Admin:
    case UserRole.SuperAdmin:
      return raw;
    default:
      return null;
  }
}

/**
 * The role name as people read it on screen. The wire value stays `SuperAdmin`
 * (the backend sends it and `UserRole` compares against it), but the product
 * spells it "Superadmin" (QA, 2026-10-23). Mapping here, rather than editing the
 * enum, keeps the display independent of what the backend transmits. Unknown or
 * empty values pass through untouched. Use this — or the `roleLabel` pipe — for
 * every role shown to a person; never print `UserRole.SuperAdmin` directly.
 */
export const SUPERADMIN_DISPLAY_NAME = 'Superadmin';

export function roleDisplayName(role: string | null | undefined): string {
  if (role === UserRole.SuperAdmin) return SUPERADMIN_DISPLAY_NAME;
  return role ?? '';
}
